import { useEffect, useState } from 'react';
import { Row, Col, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPlay, faPlus, faCircleXmark, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { getIn } from 'formik';
import XInput from '../../../component/form/XInput';
import XSelect from '../../../component/form/XSelect';
import XButton from '../../../component/button/XButton';
import useUser from './useUser';
import { userStatusOptions } from '../../../utils/options';
import XBackButton from '../../../component/button/XBackButton';
import useRoles from '../../../hooks/useRoles';
import useSidebar from '../../../hooks/useSidebar';
import PhoneNumberInput from '../../../component/phoneInput/phoneInput';
import useAxios from '../../../hooks/useAxios';
import { API_ENDPOINTS } from '../../../constants/api';
import Select, { SingleValue } from 'react-select';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import { ROLE } from '../../../utils/enums';

const LOCATION_SCOPED_ROLES = [ROLE.LOCATION_ADMIN, ROLE.LOCATION_STAFF, ROLE.READ_ONLY];

interface LocationOption {
  value: string;
  label: string;
}

const AddUser = () => {
  const { formik, countries, states, id, userData, isLoading } = useUser()
  const { roles } = useRoles()
  const { onShowSidebar } = useSidebar();
  const { fetchData } = useAxios();
  const { selectedLocationId } = useSelector((state: RootState) => state.user);
  const [locations, setLocations] = useState<any[]>([]);

  const selectedRoleId = Number(formik.values.role_id);
  const showLocationPicker = LOCATION_SCOPED_ROLES.includes(selectedRoleId);
  const allowMultipleLocations = [ROLE.LOCATION_ADMIN, ROLE.READ_ONLY].includes(selectedRoleId);
  const accessGrants = formik.values.access_grants || [];
  const isRegistered = Boolean(id && userData?.is_registration_complete);

  useEffect(() => {
    if (showLocationPicker) {
      fetchData(API_ENDPOINTS.ACCESS_GRANTS_LOCATIONS).then((response) => {
        if (response?.success) {
          setLocations(response.data ?? []);
        }
      });
    }
  }, [showLocationPicker]);

  // If role is changed to Location Staff, allow selecting only one location
  useEffect(() => {
    if (selectedRoleId === ROLE.LOCATION_STAFF && accessGrants.length > 1) {
      formik.setFieldValue('access_grants', accessGrants.slice(0, 1));
    }
    if (selectedRoleId === ROLE.READ_ONLY && accessGrants.some((g: any) => g.send_permission)) {
      formik.setFieldValue(
        'access_grants',
        accessGrants.map((g: any) => ({ ...g, send_permission: false }))
      );
    }
  }, [selectedRoleId]);

  const locationOptions: LocationOption[] = locations.map((loc: any) => ({
    value: String(loc.id),
    label: loc.display_name || loc.address || '',
  }));

  const hasAvailableLocations = accessGrants.length < locationOptions.length;

  const addLocationRow = () => {
    if (!allowMultipleLocations || !hasAvailableLocations) return;
    formik.setFieldValue('access_grants', [
      ...accessGrants,
      { location_id: '', send_permission: false },
    ]);
  };

  const removeLocationRow = (rowIndex: number) => {
    formik.setFieldValue(
      'access_grants',
      accessGrants.filter((_: any, i: number) => i !== rowIndex)
    );
  };

  useEffect(() => {
    if (showLocationPicker && accessGrants.length === 0) {
      if (!id) {
        formik.setFieldValue('access_grants', [{ location_id: selectedLocationId ? String(selectedLocationId) : '', send_permission: false }]);
      } else if (!isLoading) {
        formik.setFieldValue('access_grants', [{ location_id: '', send_permission: false }]);
      }
    }
    if (!showLocationPicker && accessGrants.length > 0) {
      formik.setFieldValue('access_grants', []);
    }
  }, [showLocationPicker, isLoading, id]);

  return (
    <div>
      <Form onSubmit={formik.handleSubmit} noValidate>
        <div className="main-title ">
          <h2 className='back-text'>
            <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}><FontAwesomeIcon icon={faBars} /></span>{id ? 'Update' : 'Add'} User</h2>
        </div>
        <div className="add-contact">
          <Row>
            {!isRegistered ? (
              <Col xl={4} md={6} xs={12}>
                <XInput label='Email Address' name='email' placeholder='Enter' type='email' formik={formik} />
              </Col>
            ) : (
              <Col xl={4} md={6} xs={12}>
                <Form.Group className="form-group">
                  <Form.Label>
                    Email Address
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="edit-user-email-tooltip" className="text-start">You cannot change this email address because the account registration is complete.</Tooltip>}
                    >
                      <span className="ms-2 cursor-pointer text-muted">
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    value={formik.values.email}
                    disabled
                    readOnly
                    title="You cannot change this email address because the account registration is complete."
                  />
                </Form.Group>
              </Col>
            )}

            <Col xl={4} md={6} xs={12}>
              <XInput label="First Name" name="first_name" placeholder="Enter" type="text" formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput label="Last Name" name="last_name" placeholder="Enter" type="text" formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <PhoneNumberInput
                value={formik.values.phone_number}
                onChange={phone => formik.setFieldValue('phone_number', phone)}
                error={formik.errors.phone_number}
                touched={formik.touched.phone_number}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput label="Address" name="address" placeholder="Enter" type="text" formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput label="ZIP Code" name="zipcode" placeholder="Enter" type="text" formik={formik} />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name='country_id'
                label="Country"
                options={Object.values(countries)}
                formik={formik}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name={`state_id`}
                label="State"
                options={Object.values(states)}
                formik={formik}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XInput name={`city`} label="City" formik={formik} placeholder="Enter" type="text"/>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name={`status_id`}
                label="Status"
                options={userStatusOptions}
                formik={formik}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
              <XSelect
                name={`role_id`}
                label="Role"
                options={Object.values(roles)}
                formik={formik}
              />
            </Col>
          </Row>

          {showLocationPicker && (
            <div className="location-access-section mt-3">
              <div className="rss-section-header">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="rss-section-title mb-0">Location Access</h6>
                  {allowMultipleLocations && hasAvailableLocations && (
                    <span className="add-link">
                      <span className="btn-add btn-add-sm cursor-pointer" onClick={addLocationRow}>
                        <FontAwesomeIcon icon={faPlus} /> Add
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {accessGrants.map((grant: any, rowIndex: number) => {
                const locationFieldName = `access_grants[${rowIndex}].location_id`;
                const sendFieldName = `access_grants[${rowIndex}].send_permission`;

                // Calculate options excluding locations selected in OTHER rows
                const selectedInOtherRows = new Set(
                  accessGrants
                    .filter((_: any, i: number) => i !== rowIndex)
                    .map((g: any) => String(g.location_id))
                    .filter(Boolean)
                );
                const availableOptions = locationOptions.filter(
                  (opt) => !selectedInOtherRows.has(opt.value)
                );
                const selectedOption = locationOptions.find(
                  (opt) => opt.value === String(grant.location_id)
                ) ?? null;
                const isInvalid = Boolean(
                  (getIn(formik.touched, locationFieldName) || formik.submitCount > 0) &&
                  getIn(formik.errors, locationFieldName)
                );

                return (
                  <Row className="mb-3 align-items-center" key={rowIndex}>
                    <Col xl={4} md={6} xs={12}>
                      <Form.Group className="form-group mb-0">
                        <Form.Label>Location</Form.Label>
                        <Select
                          classNamePrefix="location-select"
                          options={availableOptions}
                          value={selectedOption}
                          onChange={(option: SingleValue<LocationOption>) => {
                            formik.setFieldValue(locationFieldName, option ? option.value : '');
                            formik.setFieldTouched(locationFieldName, true);
                          }}
                          onBlur={() => {
                            formik.setFieldTouched(locationFieldName, true);
                          }}
                          isSearchable
                          placeholder="Select location"
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: '58px',
                              borderRadius: '6px',
                              borderColor: isInvalid ? '#dc3545' : state.isFocused ? '#86b7fe' : '#E6E6E6',
                              boxShadow: isInvalid
                                ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                                : state.isFocused
                                ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                                : 'none',
                              '&:hover': {
                                borderColor: isInvalid ? '#dc3545' : '#b3b3b3',
                              },
                              fontSize: '16px',
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: '0 16px',
                            }),
                            placeholder: (base) => ({
                              ...base,
                              color: '#878787',
                              fontSize: '16px',
                            }),
                            singleValue: (base) => ({
                              ...base,
                              color: '#000',
                              fontSize: '16px',
                            }),
                            indicatorSeparator: () => ({
                              display: 'none',
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                        />
                        {isInvalid && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {getIn(formik.errors, locationFieldName)}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xl={4} md={6} xs={12}>
                      <Form.Group className="form-group mb-0">
                        <Form.Label className="d-block d-none d-md-block">&nbsp;</Form.Label>
                        <div className="d-flex align-items-center justify-content-start gap-3" style={{ minHeight: '58px' }}>
                          <Form.Check
                            type="switch"
                            id={`${sendFieldName}-switch`}
                            label="Can Send"
                            disabled={selectedRoleId === ROLE.READ_ONLY}
                            checked={selectedRoleId === ROLE.READ_ONLY ? false : (getIn(formik.values, sendFieldName) ?? false)}
                            onChange={(e) => {
                              if (selectedRoleId !== ROLE.READ_ONLY) {
                                formik.setFieldValue(sendFieldName, e.target.checked);
                              }
                            }}
                          />
                          {allowMultipleLocations && accessGrants.length > 1 && (
                            <Button
                              variant="link"
                              className="text-danger p-0 flex-shrink-0 ms-2"
                              onClick={() => removeLocationRow(rowIndex)}
                              title="Remove location"
                            >
                              <FontAwesomeIcon icon={faCircleXmark} size="lg" />
                            </Button>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })}
            </div>
          )}
        </div>
        <div className="btns">
          <div className='d-flex justify-content-between align-items-center'>
            <XBackButton />
            <XButton loading={isLoading} type='submit' buttonText={<span>Submit<FontAwesomeIcon icon={faPlay} /></span>} className='btn-theme' />
          </div>
        </div>
      </Form>
    </div>
  )
}

export default AddUser